import {
  ASComponentSize,
  ASIconButton,
  ASIconButtonProps,
  CalendarRangeIcon,
  CloseIcon,
  ComponentSize,
  GearUserIcon,
  PodiumIcon,
  RulerIcon,
  StatsIcon,
  UserSettingsIcon,
} from '@eleventheye/asui';
import React, { MouseEvent, PointerEvent, PropsWithChildren, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getShowAdminActions, setShowAdminActions } from 'app/slices/AppUiSlice';
import styles from './IconButton.module.scss';

type ColorTheme = 'WhiteTheme' | 'GreenTheme' | 'BlueTheme';

type IconButtonOwnProps = {
  className?: string;
  ref?: React.RefObject<HTMLButtonElement>;
  size?: ASComponentSize;
  defaultLowOpacity?: boolean;
  colorTheme?: ColorTheme;
  selected?: boolean;
  disabled?: boolean;
  withText?: boolean;
  bgColorToken?: ColorTheme;
} & React.HTMLAttributes<HTMLButtonElement> &
  Pick<React.HTMLAttributes<HTMLButtonElement>, 'style' | 'onClick'>;

// 🞬 📝

const SizeStyles = {
  [ComponentSize.Small]: styles.ASSmall,
  [ComponentSize.Medium]: styles.ASMedium,
  [ComponentSize.Large]: styles.ASLarge,
};

type IconButtonProps = PropsWithChildren<IconButtonOwnProps>;
const IconButton: React.FC<IconButtonProps> = ({
  className = '',
  children,
  size = ComponentSize.Small,

  selected = false,
  withText = false,
  colorTheme = 'WhiteTheme',
  disabled,
  bgColorToken = 'WhiteTheme',
  ...props
}) => {
  const baseCls = `${styles.IconButton} ${className} ${colorTheme} ${bgColorToken}`;
  const withTextCls = withText ? styles.WithText : styles.WithoutText;
  const selectedCls = selected ? styles.Selected : '';

  return (
    <button
      type="button"
      {...props}
      disabled={disabled}
      className={`${baseCls} s${selectedCls} ${SizeStyles[size]} ${withTextCls}`}
    >
      {children}
    </button>
  );
};

// eslint-disable-next-line react/prop-types
const CloseButton: React.FC<IconButtonProps> = ({
  className = '',

  size = ComponentSize.Small,
  ...props
}) => {
  const onCloseHandler = (e: MouseEvent<HTMLButtonElement>) => {
    if (props.onClick) props.onClick(e);
  };

  const btnSize = size === ComponentSize.Small ? 20 : size === ComponentSize.Medium ? 30 : 40;

  return (
    <IconButton
      {...props}
      onClick={onCloseHandler}
      size={size}
      className={`${className || ''} ${styles.CloseButton} ${styles.CloseButtonPosition}`}
    >
      <CloseIcon size={btnSize} iconSize={btnSize} color="#585858" fillColor="#585858" />
    </IconButton>
  );
};

const UserAccountButton: React.FC<ASIconButtonProps> = ({
  className = '',
  iconSize = 30,
  hidden = false,
  size = ComponentSize.Medium,
  icon = UserSettingsIcon,
  fillColor = '#585858',
  color = 'transparent',
  selected = false,
  onIconButtonTap = () => undefined,
  ...props
}) => {
  const [isSelected, setIsSelected] = useState(selected);

  const onClickedHandler = (e: PointerEvent) => {
    onIconButtonTap(e);
    setIsSelected(selected);
  };

  return (
    <ASIconButton
      {...props}
      onIconButtonTap={onClickedHandler}
      size={size}
      iconSize={iconSize}
      hidden={hidden}
      icon={icon}
      strokeWidth={0}
      fillColor={fillColor}
      color={color}
      selected={isSelected}
      className={`${className} ${styles.UserAccountButton}`}
    ></ASIconButton>
  );
};

const ShowWeeklyGamesButton: React.FC<ASIconButtonProps> = ({
  className = '',
  iconSize = 30,
  size = ComponentSize.Medium,
  icon = CalendarRangeIcon,
  fillColor = '#585858',
  color = 'transparent',
  selected = false,
  onIconButtonTap = () => undefined,
  ...props
}) => {
  const [isSelected, setIsSelected] = useState(selected);

  const onClickedHandler = (e: PointerEvent) => {
    onIconButtonTap(e);
    setIsSelected(selected);
  };

  useEffect(() => {
    setIsSelected(selected);
  }, [selected]);

  return (
    <ASIconButton
      {...props}
      selected={isSelected}
      onIconButtonTap={onClickedHandler}
      iconSize={iconSize}
      size={size}
      strokeWidth={0}
      icon={icon}
      fillColor={fillColor}
      color={color}
      className={`${className} ${styles.UserAccountButton}`}
    >
      {/* <CalendarRangeIcon size={btnSize} color="#585858" fillColor="#585858" style={{ scale: isSelected ? 0.5 : 1 }} /> */}
    </ASIconButton>
  );
};

const ShowStandingsButton: React.FC<ASIconButtonProps> = ({
  className = '',
  iconSize = 30,
  size = ComponentSize.Medium,
  icon = PodiumIcon,
  fillColor = '#585858',
  color = 'transparent',
  selected = false,
  onIconButtonTap = () => undefined,
  ...props
}) => {
  const [isSelected, setIsSelected] = useState(selected);

  const onClickedHandler = (e: PointerEvent) => {
    onIconButtonTap(e);
    setIsSelected(selected);
  };

  useEffect(() => {
    setIsSelected(selected);
  }, [selected]);

  return (
    <ASIconButton
      {...props}
      selected={isSelected}
      onIconButtonTap={onClickedHandler}
      iconSize={iconSize}
      size={size}
      icon={icon}
      strokeWidth={0}
      fillColor={fillColor}
      color={color}
      className={`${className} ${styles.UserAccountButton}`}
    >
      {/* <PodiumIcon size={btnSize} color="#585858" fillColor="#585858" style={{ scale: isSelected ? 0.5 : 1 }} /> */}
    </ASIconButton>
  );
};

const ShowStatsButton: React.FC<ASIconButtonProps> = ({
  className = '',
  iconSize = 30,
  size = ComponentSize.Medium,
  icon = StatsIcon,
  fillColor = '#585858',
  color = 'transparent',
  selected = false,
  onIconButtonTap = () => undefined,
  ...props
}) => {
  const [isSelected, setIsSelected] = useState(false);

  const onClickedHandler = (e: PointerEvent) => {
    onIconButtonTap(e);
    setIsSelected(selected);
  };

  useEffect(() => {
    setIsSelected(selected);
  }, [selected]);

  return (
    <ASIconButton
      {...props}
      selected={isSelected}
      onIconButtonTap={onClickedHandler}
      iconSize={iconSize}
      size={size}
      strokeWidth={0}
      icon={icon}
      fillColor={fillColor}
      color={color}
      className={`${className} ${styles.UserAccountButton}`}
    >
      {/* `<icon size={btnSize} color="#585858" fillColor="#585858" style={{ scale: isSelected ? 0.5 : 1 }} />` */}
    </ASIconButton>
  );
};

const ShowHeadToHeadButton: React.FC<ASIconButtonProps> = ({
  className = '',
  iconSize = 30,
  size = ComponentSize.Medium,
  icon = RulerIcon,
  fillColor = '#585858',
  color = 'transparent',
  selected = false,
  onIconButtonTap = () => undefined,
  ...props
}) => {
  const [isSelected, setIsSelected] = useState(false);

  const onClickedHandler = (e: PointerEvent) => {
    onIconButtonTap(e);
    setIsSelected(selected);
  };

  useEffect(() => {
    setIsSelected(selected);
  }, [selected]);

  return (
    <ASIconButton
      {...props}
      selected={isSelected}
      onIconButtonTap={onClickedHandler}
      iconSize={iconSize}
      size={size}
      strokeWidth={0}
      icon={icon}
      fillColor={fillColor}
      color={color}
      className={`${className} ${styles.UserAccountButton}`}
    >
      {/* `<icon size={btnSize} color="#585858" fillColor="#585858" style={{ scale: isSelected ? 0.5 : 1 }} />` */}
    </ASIconButton>
  );
};

const ShowAdminActionsButton: React.FC<ASIconButtonProps> = ({
  className = '',
  iconSize = 30,
  size = ComponentSize.Medium,
  icon = GearUserIcon,
  fillColor = '#585858',
  color = 'transparent',
  selected = false,
  onIconButtonTap = () => undefined,
  ...props
}) => {
  const [isSelected, setIsSelected] = useState(false);
  const showAdminActions = useAppSelector(getShowAdminActions);
  const dispatch = useAppDispatch();

  const onClickedHandler = (e: PointerEvent) => {
    onIconButtonTap(e);
    dispatch(setShowAdminActions(!selected));
  };

  useEffect(() => {
    setIsSelected(showAdminActions);
  }, [showAdminActions]);

  return (
    <ASIconButton
      {...props}
      selected={isSelected}
      onIconButtonTap={onClickedHandler}
      iconSize={iconSize}
      size={size}
      strokeWidth={0}
      icon={icon}
      fillColor={fillColor}
      color={color}
      className={`${className} ${styles.UserAccountButton}`}
    >
      {/* `<icon size={btnSize} color="#585858" fillColor="#585858" style={{ scale: isSelected ? 0.5 : 1 }} />` */}
    </ASIconButton>
  );
};

// type FullScreenButtonProps = {
//   isPainting?: boolean;
// } & IconButtonOwnProps;

// const FullScreenButton: React.FC<FullScreenButtonProps> = ({
//   className = '',
//   isPainting = false,
//   // eslint-disable-next-line react/prop-types
//   onClick,
//   ...props
// }) => {
//   const positionCls = isPainting ? styles.FullScreenButtonPaintingPosition : styles.FullScreenButtonFramePosition;

//   const onCloseHandler = (e: MouseEvent<HTMLButtonElement>) => {
//     if (onClick) onClick(e);
//   };

//   return (
//     <IconButton
//       {...props}
//       onClick={onCloseHandler}
//       size={props.size || ComponentSize.Small}
//       className={`${className || ''} ${styles.FullScreenButton} ${positionCls}`}
//     >
//       {Emojis.fullWindow}
//     </IconButton>
//   );
// };

export {
  CloseButton,
  IconButton as default,
  // FullScreenButton,
  ShowAdminActionsButton,
  ShowHeadToHeadButton,
  ShowStandingsButton,
  ShowStatsButton,
  ShowWeeklyGamesButton,
  UserAccountButton,
};
